import { useState } from "react";
import { motion } from "framer-motion";
import { Upload, CheckCircle, Loader2, FileText, X } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const serviceTypes = [
  { value: "impression-dtf", label: "Impression DTF (t-shirts, textiles)" },
  { value: "sublimation", label: "Sublimation (tasses, gourdes, objets)" },
  { value: "serigraphie", label: "Sérigraphie (grandes séries)" },
  { value: "flocage-flex", label: "Flocage & Flex (marquage textile)" },
  { value: "gravure-plaque", label: "Gravure & Plaques (plexiglass)" },
  { value: "infographie", label: "Infographie (cartes, affiches, kakémonos)" },
  { value: "autre", label: "Autre / Plusieurs services" }
];

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
};

export default function QuotePage() {
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    service_type: "",
    quantity: "",
    dimensions: "",
    deadline: "",
    message: ""
  });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.size > 10 * 1024 * 1024) {
        toast.error("Le fichier est trop volumineux (max 10Mo)");
        return;
      }
      setFile(selectedFile);
    }
  };

  const removeFile = () => {
    setFile(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || !formData.service_type) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    setLoading(true);

    try {
      const formDataToSend = new FormData();
      
      // Append only non-empty values
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== undefined && formData[key] !== "") {
          formDataToSend.append(key, formData[key]);
        }
      });
      
      // Append file if present
      if (file && file instanceof File) {
        formDataToSend.append("file", file, file.name);
      }

      await axios.post(`${API}/quote`, formDataToSend);

      setSuccess(true);
      toast.success("Demande envoyée avec succès !");
      
      // Reset form
      setFormData({
        name: "",
        company: "",
        email: "",
        phone: "",
        service_type: "",
        quantity: "",
        dimensions: "",
        deadline: "",
        message: ""
      });
      setFile(null);

    } catch (error) {
      console.error("Error submitting quote:", error);
      toast.error("Erreur lors de l'envoi. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20" data-testid="quote-success">
        <motion.div 
          className="text-center max-w-md px-6"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <div className="w-20 h-20 bg-[hsl(var(--accent-green))] flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={40} className="text-background" />
          </div>
          <h1 className="text-3xl font-bold mb-4">Demande envoyée !</h1>
          <p className="text-muted mb-8">
            Nous avons bien reçu votre demande de devis. 
            Notre équipe vous contactera sous 24-48h ouvrées.
          </p>
          <button 
            onClick={() => setSuccess(false)} 
            className="btn-outline"
            data-testid="new-quote-btn"
          >
            Nouvelle demande
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div data-testid="quote-page">
      {/* Hero */}
      <section className="pt-32 pb-16 bg-secondary/30" data-testid="quote-hero">
        <div className="container-custom">
          <motion.div {...fadeInUp}>
            <p className="eyebrow mb-3">Contact</p>
            <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="quote-title">
              Demander un devis
            </h1>
            <p className="text-muted text-lg max-w-2xl">
              Décrivez votre projet et recevez une proposition personnalisée. 
              Réponse garantie sous 24-48h ouvrées.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Form */}
      <section className="section-padding" data-testid="quote-form-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Form */}
            <motion.div className="lg:col-span-2" {...fadeInUp}>
              <form onSubmit={handleSubmit} className="space-y-8" data-testid="quote-form">
                {/* Contact Info */}
                <div>
                  <h2 className="text-xl font-bold mb-6">Vos coordonnées</h2>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Nom / Prénom <span className="text-[hsl(var(--accent-red))]">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="input-swiss"
                        placeholder="Jean Dupont"
                        required
                        data-testid="input-name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Entreprise</label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="input-swiss"
                        placeholder="Nom de votre entreprise"
                        data-testid="input-company"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Email <span className="text-[hsl(var(--accent-red))]">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="input-swiss"
                        placeholder="jean@exemple.ch"
                        required
                        data-testid="input-email"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Téléphone <span className="text-[hsl(var(--accent-red))]">*</span>
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="input-swiss"
                        placeholder="+41 79 000 00 00"
                        required
                        data-testid="input-phone"
                      />
                    </div>
                  </div>
                </div>

                {/* Project Info */}
                <div>
                  <h2 className="text-xl font-bold mb-6">Votre projet</h2>
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Type de service <span className="text-[hsl(var(--accent-red))]">*</span>
                      </label>
                      <select
                        name="service_type"
                        value={formData.service_type}
                        onChange={handleChange}
                        className="input-swiss cursor-pointer bg-transparent"
                        required
                        data-testid="select-service"
                      >
                        <option value="">Sélectionnez un service</option>
                        {serviceTypes.map(service => (
                          <option key={service.value} value={service.value}>
                            {service.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium mb-2">Quantité</label>
                        <input
                          type="text"
                          name="quantity"
                          value={formData.quantity}
                          onChange={handleChange}
                          className="input-swiss"
                          placeholder="Ex: 50 pièces, 100 t-shirts..."
                          data-testid="input-quantity"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Dimensions / Format</label>
                        <input
                          type="text"
                          name="dimensions"
                          value={formData.dimensions}
                          onChange={handleChange}
                          className="input-swiss"
                          placeholder="Ex: A4, 10x15cm, taille M..."
                          data-testid="input-dimensions"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Deadline souhaitée</label>
                      <input
                        type="text"
                        name="deadline"
                        value={formData.deadline}
                        onChange={handleChange}
                        className="input-swiss"
                        placeholder="Ex: Fin janvier, urgent, flexible..."
                        data-testid="input-deadline"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Message / Détails du projet</label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        className="input-swiss min-h-[150px] resize-y"
                        placeholder="Décrivez votre projet, vos attentes, et toute information utile..."
                        data-testid="input-message"
                      />
                    </div>

                    {/* File Upload */}
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Fichier joint <span className="text-muted">(optionnel, max 10Mo)</span>
                      </label>
                      {file ? (
                        <div className="border border-border p-4 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <FileText size={20} className="text-muted" />
                            <span className="text-sm">{file.name}</span>
                          </div>
                          <button 
                            type="button" 
                            onClick={removeFile}
                            className="text-muted hover:text-foreground"
                            data-testid="remove-file-btn"
                          >
                            <X size={18} />
                          </button>
                        </div>
                      ) : (
                        <label className="file-upload block" data-testid="file-upload">
                          <input
                            type="file"
                            onChange={handleFileChange}
                            className="hidden"
                            accept=".pdf,.jpg,.jpeg,.png,.ai,.eps,.svg"
                            data-testid="file-input"
                          />
                          <Upload size={24} className="mx-auto mb-2 text-muted" />
                          <p className="text-sm text-muted">
                            Cliquez ou glissez votre fichier ici
                          </p>
                          <p className="text-xs text-muted mt-1">
                            PDF, JPG, PNG, AI, EPS, SVG
                          </p>
                        </label>
                      )}
                    </div>
                  </div>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full md:w-auto flex items-center justify-center gap-2"
                  data-testid="submit-quote-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Envoi en cours...
                    </>
                  ) : (
                    "Envoyer ma demande"
                  )}
                </button>
              </form>
            </motion.div>

            {/* Sidebar */}
            <motion.div 
              className="lg:col-span-1"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="bg-foreground text-background p-8 sticky top-32">
                <h3 className="font-bold text-lg mb-6">Ce qui se passe ensuite</h3>
                <div className="space-y-6">
                  <div className="flex gap-4">
                    <span className="font-mono text-2xl text-background/30">01</span>
                    <div>
                      <h4 className="font-bold mb-1">Réception</h4>
                      <p className="text-background/70 text-sm">
                        Nous recevons votre demande et analysons votre projet.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <span className="font-mono text-2xl text-background/30">02</span>
                    <div>
                      <h4 className="font-bold mb-1">Échange</h4>
                      <p className="text-background/70 text-sm">
                        Si besoin, nous vous contactons pour préciser les détails.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <span className="font-mono text-2xl text-background/30">03</span>
                    <div>
                      <h4 className="font-bold mb-1">Devis</h4>
                      <p className="text-background/70 text-sm">
                        Vous recevez un devis détaillé sous 24-48h ouvrées.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="border-t border-background/20 mt-8 pt-8">
                  <p className="text-sm text-background/70 mb-4">
                    Une question urgente ?
                  </p>
                  <p className="font-mono">0696 60 13 17</p>
                  <p className="text-sm text-background/50 mt-2">Lun-Ven 8h-18h</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
